/**
 * WebGL2 full-screen post-processing over the Canvas2D scene.
 *
 * The 2D renderer draws the world into the #game canvas as before; when any
 * shader effect is active, PostFx uploads that canvas as a texture and renders
 * it through an über-shader into an overlay canvas (distortions → chromatic
 * sampling → color ops). With no active effects the overlay is hidden and the
 * cost is zero. If WebGL2 is unavailable the game remains fully playable —
 * effects simply don't display (warned once, never silently pretended).
 */

const VERT = `#version 300 es
layout(location=0) in vec2 aPos;
out vec2 vUv;
void main() {
  vUv = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG = `#version 300 es
precision highp float;
in vec2 vUv;
out vec4 outColor;
uniform sampler2D uScene;
uniform vec2 uRes;
uniform vec2 uCenter;      // player position in uv space
uniform float uTime;
uniform float uChromatic;  // 0..1
uniform float uPixelate;   // pixel size (0 = off)
uniform float uGray;       // 0..1
uniform float uSepia;      // 0..1
uniform float uInvert;     // 0..1
uniform float uHue;        // degrees
uniform float uBulge;      // -1..1 (negative pinches)
uniform float uShock;      // wave progress 0..1
uniform float uShockAmp;   // wave amplitude
uniform float uGlitch;     // 0..1
uniform float uShockline;  // vertical-wave progress 0..1
uniform float uShocklineAmp;
uniform float uRadial;     // radial blur 0..1
uniform float uMotion;     // horizontal motion blur 0..1
uniform float uLens;       // lens-circle vignette 0..1
uniform float uSplit;      // mirrored split-screen 0..1

vec3 hueRotate(vec3 c, float deg) {
  float a = radians(deg);
  float ca = cos(a); float sa = sin(a);
  mat3 m = mat3(
    0.213 + ca*0.787 - sa*0.213, 0.715 - ca*0.715 - sa*0.715, 0.072 - ca*0.072 + sa*0.928,
    0.213 - ca*0.213 + sa*0.143, 0.715 + ca*0.285 + sa*0.140, 0.072 - ca*0.072 - sa*0.283,
    0.213 - ca*0.213 - sa*0.787, 0.715 - ca*0.715 + sa*0.715, 0.072 + ca*0.928 + sa*0.072);
  return clamp(c * m, 0.0, 1.0);
}

float hash(float n) { return fract(sin(n) * 43758.5453123); }

void main() {
  vec2 uv = vUv;
  float aspect = uRes.x / uRes.y;

  // --- Coordinate distortions ------------------------------------------------
  if (uSplit > 0.0) {
    // Mirrored split about the screen center, blended in by strength.
    vec2 mirrored = vec2(uv.x < 0.5 ? uv.x : 1.0 - uv.x, uv.y);
    uv = mix(uv, mirrored, clamp(uSplit, 0.0, 1.0));
  }
  if (uBulge != 0.0) {
    vec2 d = uv - uCenter;
    d.x *= aspect;
    float r = length(d);
    float k = 1.0 + uBulge * exp(-r * 3.0);
    d /= k;
    d.x /= aspect;
    uv = uCenter + d;
  }
  if (uShock > 0.0 && uShockAmp > 0.0) {
    vec2 d = uv - uCenter;
    d.x *= aspect;
    float r = length(d);
    float wave = uShock * 1.4;                    // wavefront radius
    float band = r - wave;
    float ring = exp(-band * band * 120.0);       // narrow gaussian ring
    float push = ring * uShockAmp * 0.04 * (1.0 - uShock);
    vec2 dir = r > 0.0001 ? d / r : vec2(0.0);
    dir.x /= aspect;
    uv -= dir * push;
  }
  if (uShockline > 0.0 && uShocklineAmp > 0.0) {
    // Vertical line wave sweeping outward horizontally from the player.
    float wave = uShockline * 1.2;
    float band = abs(uv.x - uCenter.x) - wave;
    float ridge = exp(-band * band * 200.0);
    uv.y += ridge * uShocklineAmp * 0.03 * (1.0 - uShockline);
  }
  if (uGlitch > 0.0) {
    float row = floor(uv.y * 40.0);
    float jitter = (hash(row + floor(uTime * 17.0) * 7.0) - 0.5);
    if (abs(jitter) > 0.5 - uGlitch * 0.35) {
      uv.x += jitter * uGlitch * 0.08;
    }
  }
  if (uPixelate > 0.5) {
    vec2 px = vec2(uPixelate) / uRes;
    uv = (floor(uv / px) + 0.5) * px;
  }
  uv = clamp(uv, 0.0, 1.0);

  // --- Sampling (chromatic aberration = shifted channel taps) ----------------
  vec3 col;
  if (uChromatic > 0.0) {
    vec2 dir = (uv - uCenter) * uChromatic * 0.02;
    col.r = texture(uScene, clamp(uv + dir, 0.0, 1.0)).r;
    col.g = texture(uScene, uv).g;
    col.b = texture(uScene, clamp(uv - dir, 0.0, 1.0)).b;
  } else {
    col = texture(uScene, uv).rgb;
  }
  if (uRadial > 0.0) {
    // 6-tap radial blur toward the player.
    vec2 toC = (uCenter - uv) * uRadial * 0.06;
    vec3 acc = col;
    for (int i = 1; i <= 6; i++) {
      acc += texture(uScene, clamp(uv + toC * (float(i) / 6.0), 0.0, 1.0)).rgb;
    }
    col = acc / 7.0;
  }
  if (uMotion > 0.0) {
    // 6-tap horizontal motion blur.
    vec2 stepv = vec2(uMotion * 0.02, 0.0);
    vec3 acc = col;
    for (int i = 1; i <= 3; i++) {
      acc += texture(uScene, clamp(uv + stepv * float(i), 0.0, 1.0)).rgb;
      acc += texture(uScene, clamp(uv - stepv * float(i), 0.0, 1.0)).rgb;
    }
    col = acc / 7.0;
  }

  // --- Color operations -------------------------------------------------------
  if (uHue != 0.0) col = hueRotate(col, uHue);
  if (uSepia > 0.0) {
    vec3 sep = vec3(dot(col, vec3(0.393, 0.769, 0.189)),
                    dot(col, vec3(0.349, 0.686, 0.168)),
                    dot(col, vec3(0.272, 0.534, 0.131)));
    col = mix(col, clamp(sep, 0.0, 1.0), uSepia);
  }
  if (uGray > 0.0) {
    float g = dot(col, vec3(0.299, 0.587, 0.114));
    col = mix(col, vec3(g), uGray);
  }
  col = mix(col, 1.0 - col, uInvert);
  if (uLens > 0.0) {
    // Lens circle: darken outside a soft circle around the player.
    vec2 d = vUv - uCenter;
    d.x *= aspect;
    float vig = smoothstep(0.25, 0.8, length(d));
    col *= 1.0 - vig * uLens;
  }

  outColor = vec4(col, 1.0);
}`;

const UNIFORMS = [
  'uScene',
  'uRes',
  'uCenter',
  'uTime',
  'uChromatic',
  'uPixelate',
  'uGray',
  'uSepia',
  'uInvert',
  'uHue',
  'uBulge',
  'uShock',
  'uShockAmp',
  'uGlitch',
  'uShockline',
  'uShocklineAmp',
  'uRadial',
  'uMotion',
  'uLens',
  'uSplit',
] as const;
type UniformName = (typeof UNIFORMS)[number];

export class PostFx {
  private gl: WebGL2RenderingContext | null = null;
  private overlay: HTMLCanvasElement;
  private tex: WebGLTexture | null = null;
  private loc = {} as Record<UniformName, WebGLUniformLocation | null>;
  private warned = false;
  private visible = false;

  constructor(private source: HTMLCanvasElement) {
    this.overlay = document.createElement('canvas');
    this.overlay.id = 'fx';
    this.overlay.style.cssText =
      'position:fixed;inset:0;width:100vw;height:100vh;pointer-events:none;display:none;';
    source.insertAdjacentElement('afterend', this.overlay);
    this.init();
  }

  private init(): void {
    const gl = this.overlay.getContext('webgl2', { premultipliedAlpha: false });
    if (!gl) return; // handled lazily in render() with a one-time warning
    this.gl = gl;

    const compile = (type: number, src: string): WebGLShader => {
      const sh = gl.createShader(type);
      if (!sh) throw new Error('PostFx: shader allocation failed');
      gl.shaderSource(sh, src);
      gl.compileShader(sh);
      if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
        throw new Error(`PostFx shader compile failed: ${gl.getShaderInfoLog(sh)}`);
      }
      return sh;
    };
    const prog = gl.createProgram();
    if (!prog) throw new Error('PostFx: program allocation failed');
    gl.attachShader(prog, compile(gl.VERTEX_SHADER, VERT));
    gl.attachShader(prog, compile(gl.FRAGMENT_SHADER, FRAG));
    gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
      throw new Error(`PostFx link failed: ${gl.getProgramInfoLog(prog)}`);
    }
    gl.useProgram(prog);
    for (const name of UNIFORMS) this.loc[name] = gl.getUniformLocation(prog, name);

    // Fullscreen triangle-strip quad.
    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);

    this.tex = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, this.tex);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.uniform1i(this.loc.uScene, 0);
  }

  /** Apply active effects for this frame; hides itself when none are active. */
  render(
    fx: Readonly<Record<string, number>>,
    playerScreenX: number,
    playerScreenY: number,
    timeS: number,
  ): void {
    const active = Object.keys(fx).length > 0;
    if (!active) {
      if (this.visible) {
        this.overlay.style.display = 'none';
        this.visible = false;
      }
      return;
    }
    const gl = this.gl;
    if (!gl) {
      if (!this.warned) {
        console.warn('WebGL2 unavailable: shader effects will not display (gameplay unaffected).');
        this.warned = true;
      }
      return;
    }
    if (!this.visible) {
      this.overlay.style.display = 'block';
      this.visible = true;
    }
    const w = this.source.width;
    const h = this.source.height;
    if (this.overlay.width !== w || this.overlay.height !== h) {
      this.overlay.width = w;
      this.overlay.height = h;
    }
    gl.viewport(0, 0, w, h);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.tex);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, this.source);

    gl.uniform2f(this.loc.uRes, w, h);
    gl.uniform2f(this.loc.uCenter, playerScreenX / w, 1 - playerScreenY / h);
    gl.uniform1f(this.loc.uTime, timeS);
    gl.uniform1f(this.loc.uChromatic, fx.chromatic ?? 0);
    gl.uniform1f(this.loc.uPixelate, fx.pixelate ?? 0);
    gl.uniform1f(this.loc.uGray, fx.grayscale ?? 0);
    gl.uniform1f(this.loc.uSepia, fx.sepia ?? 0);
    gl.uniform1f(this.loc.uInvert, fx.invert ?? 0);
    gl.uniform1f(this.loc.uHue, fx.hue ?? 0);
    gl.uniform1f(this.loc.uBulge, fx.bulge ?? 0);
    gl.uniform1f(this.loc.uShock, fx.shockwave ?? 0);
    gl.uniform1f(this.loc.uShockAmp, fx.shockwaveAmp ?? 0);
    gl.uniform1f(this.loc.uGlitch, fx.glitch ?? 0);
    gl.uniform1f(this.loc.uShockline, fx.shockline ?? 0);
    gl.uniform1f(this.loc.uShocklineAmp, fx.shocklineAmp ?? 0);
    gl.uniform1f(this.loc.uRadial, fx.radial ?? 0);
    gl.uniform1f(this.loc.uMotion, fx.motion ?? 0);
    gl.uniform1f(this.loc.uLens, fx.lens ?? 0);
    gl.uniform1f(this.loc.uSplit, fx.split ?? 0);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
  }
}
